// Read-only list of top posts. NEVER renders message text or caption per
// §15.3 — only metadata (length bucket + media label) and aggregated
// engagement (reactions + replies). Sorted server-side; this component
// just walks the list as-is.
//
// Accepts a structural `TopPostListItem` shape so both
// `CommunityOverviewTopPost` (TASK-027 top-5) and `ContentTopPost`
// (TASK-034 top-10) can be passed in. Both schemas already share these
// fields verbatim — the structural prop type pins the contract here so a
// future divergence in either schema surfaces as a compile error in this
// component, not silently rendering an `undefined`.

import type { MediaType } from "@community-analytics/shared";

import { cn } from "@shared/lib";

import { lengthBucket, mediaLabel } from "../lib/format";

/**
 * Structural shape this component reads. Both
 * `CommunityOverviewTopPost` and `ContentTopPost` are assignable.
 */
export interface TopPostListItem {
  telegramMessageId: string;
  contentLength: number;
  mediaType: MediaType | null;
  isReply: boolean;
  isForwarded: boolean;
  reactionsCount: number;
  repliesCount: number;
}

export interface TopPostsListProps {
  posts: ReadonlyArray<TopPostListItem>;
  /** Externalized strings — see TASK-030 AC. */
  emptyLabel: string;
  reactionsLabel: string;
  repliesLabel: string;
  className?: string;
}

export function TopPostsList({
  posts,
  emptyLabel,
  reactionsLabel,
  repliesLabel,
  className,
}: TopPostsListProps) {
  if (posts.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed bg-background py-8 text-sm text-muted-foreground",
          className,
        )}
        role="status"
      >
        {emptyLabel}
      </div>
    );
  }
  return (
    <ol className={cn("flex flex-col gap-2", className)}>
      {posts.map((p, idx) => (
        <li
          key={p.telegramMessageId}
          className="flex items-center justify-between gap-3 rounded-lg border bg-background px-4 py-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums text-muted-foreground">
              {idx + 1}
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium">
                {mediaLabel(p.mediaType)}
                {p.isReply ? " · Reply" : ""}
                {p.isForwarded ? " · Forwarded" : ""}
              </span>
              <span className="text-xs text-muted-foreground">
                {lengthBucket(p.contentLength)} · {p.contentLength}c
              </span>
            </div>
          </div>
          <div className="flex flex-none flex-col items-end text-xs tabular-nums text-muted-foreground">
            <span>
              <span className="font-semibold text-foreground">
                {p.reactionsCount}
              </span>{" "}
              {reactionsLabel}
            </span>
            <span>
              <span className="font-semibold text-foreground">
                {p.repliesCount}
              </span>{" "}
              {repliesLabel}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}
